import { useEffect, useMemo, useRef, useState } from 'react'
import { Delete, Scale, Wallet } from 'lucide-react'
import clsx from 'clsx'

import { Modal } from '@/componentes/ui/Modal'
import { cantidadMilesimas, plataCentavos, plataCorta } from '@/lib/formato'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import { cantidadPorImporte } from '@/lib/calculoVenta'
import { importeLinea } from './useTicket'
import type { LecturaBalanza, ProductoPos } from './tipos'
import { useTecladoNumerico } from './useTecladoNumerico'
import { useEnterConfirma } from './useEnterConfirma'

type Modo = 'cantidad' | 'importe'

interface Props {
  producto: ProductoPos
  /** Cantidad actual, cuando se está corrigiendo una línea ya cargada. */
  inicialMilesimas?: number
  /** Lectura de la balanza, si el runtime la tiene. Null en la web. */
  balanza: LecturaBalanza | null
  onConfirmar: (r: { cantidadMilesimas?: number; importeObjetivoCentavos?: number }) => void
  onCerrar: () => void
}

// Pide la cantidad de un producto pesable.
//
// Tiene los tres caminos que se usan de verdad en un mostrador, y los tres
// importan:
//
//   1. La balanza en vivo, cuando hay: se apoya la mercadería y el peso
//      aparece. Solo se puede confirmar con la lectura ESTABLE, porque tomar
//      el peso mientras se apoya da de menos y es el error que el cliente nota.
//   2. Tipeado a mano: es el camino de la versión web, y el de la caja cuando
//      la balanza está desenchufada. Nunca puede faltar.
//   3. Por importe: «dame $2.000 de papa». Se usa tanto como la venta por
//      peso, y el sistema que no lo tiene obliga al cajero a hacer la cuenta.
export function DialogoCantidad({
  producto,
  inicialMilesimas,
  balanza,
  onConfirmar,
  onCerrar,
}: Props) {
  useAyudaDe('pos.cantidad')

  const decimales = producto.decimalesCantidad
  const hayBalanza = balanza?.conectada === true

  const [modo, setModo] = useState<Modo>('cantidad')

  // El valor se maneja como TEXTO de dígitos y no como número: es un teclado
  // de calculadora, donde tipear «1250» significa 1,250 y no mil doscientos
  // cincuenta. Con un número de por medio, el cursor y los ceros a la
  // izquierda se vuelven imposibles de manejar.
  const [digitos, setDigitos] = useState(() =>
    inicialMilesimas ? String(inicialMilesimas) : '',
  )

  const refInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    refInput.current?.focus()
  }, [modo])

  // Y el teclado de la máquina carga los mismos dígitos que el de la pantalla.
  // El input es `readOnly` a propósito (para que la tablet no abra el teclado
  // del sistema), así que sin esto una caja con teclado tenía que cargar el
  // peso con el mouse.
  useTecladoNumerico(setDigitos)

  const milesimas = useMemo(() => {
    const n = Number(digitos || '0')

    // Los dígitos se interpretan en la escala de la unidad: con 3 decimales,
    // «1250» son 1,250 kg; con 0 decimales, «2» son 2 unidades.
    return decimales === 0 ? n * 1000 : n * Math.pow(10, 3 - decimales)
  }, [digitos, decimales])

  const centavos = useMemo(() => Number(digitos || '0') * 100, [digitos])

  const pesoBalanza = useMemo(() => {
    if (!balanza?.hayLectura || balanza.gramos == null) return null

    // Se descuenta la tara del envase si el producto la declara: la bandeja
    // pesa y el cliente no la compra.
    return Math.max(0, balanza.gramos - (producto.taraGramos ?? 0))
  }, [balanza, producto.taraGramos])

  // El peso de la balanza sirve para confirmar cuando la lectura está
  // ESTABLE, que es la misma regla del botón grande: tomar el peso mientras se
  // apoya la mercadería da de menos, y esa diferencia la nota el cliente.
  const pesoUsable =
    modo === 'cantidad' && balanza?.estable === true && pesoBalanza !== null && pesoBalanza > 0

  const puedeConfirmar =
    modo === 'cantidad'
      ? milesimas > 0 || pesoUsable
      : centavos > 0 && producto.precioCentavos > 0

  const confirmar = () => {
    if (!puedeConfirmar) return

    if (modo === 'importe') {
      onConfirmar({ importeObjetivoCentavos: centavos })
      return
    }

    // Lo tipeado GANA sobre la balanza: si el cajero puso un número, es el que
    // quiere. La balanza entra cuando no se tipeó nada, que es el camino
    // normal —se apoya la mercadería y se aprieta Enter— y hasta ahora no
    // existía: el peso solo se podía tomar tocando el cuadro con el mouse.
    if (milesimas > 0) onConfirmar({ cantidadMilesimas: milesimas })
    else if (pesoUsable) onConfirmar({ cantidadMilesimas: pesoBalanza ?? 0 })
  }

  // Las flechas cambian de «por peso» a «por importe».
  //
  // «Dame $2.000 de papa» se pide tanto como el peso, y era el único paso de
  // este diálogo que obligaba al mouse: los dígitos entran por el teclado y el
  // Enter agrega, pero la solapa había que tocarla. Van las flechas y no una
  // letra porque el campo es `readOnly` y no hay nada más que navegar acá
  // adentro, así que la tecla no le saca lugar a nada.
  useEffect(() => {
    function alApretar(e: KeyboardEvent) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (e.ctrlKey || e.altKey || e.metaKey) return

      e.preventDefault()
      setModo(e.key === 'ArrowRight' ? 'importe' : 'cantidad')
    }

    window.addEventListener('keydown', alApretar)
    return () => window.removeEventListener('keydown', alApretar)
  }, [])

  // Enter agrega al ticket. El peso o el importe se cargan con el teclado
  // numérico —el de la máquina, no el de la pantalla— así que terminar de
  // tipear y tener que ir al mouse para el último paso es justo el paso de más
  // que se repite en cada renglón del día. Es el mismo Enter que ya cobra en
  // los dos diálogos de cobro, y por eso el hook es el mismo.
  useEnterConfirma(confirmar, { activo: puedeConfirmar })

  return (
    <Modal
      onCerrar={onCerrar}
      titulo={producto.nombre}
      descripcion={`${plataCentavos(producto.precioCentavos)} por ${producto.unidadNombre.toLowerCase()}`}
      ancho="md"
      // La acción principal va en el PIE y no en el cuerpo. Es el gotcha que el
      // sistema ya pagó tres veces (el diálogo de cobro, el del cobro sin
      // detalle y el del movimiento de cajón): el `Modal` se acota al alto de
      // la ventana y reparte por flex, así que el pie no scrollea nunca, pero
      // un botón armado adentro del cuerpo sí —y en un teléfono, con el peso,
      // los atajos de cantidad y el teclado apilados, «Agregar al ticket»
      // quedaba abajo del borde—.
      pie={
        <div className="flex w-full gap-2">
          <button type="button" onClick={onCerrar} className="btn-secundario flex-1">
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={!puedeConfirmar}
            className="btn-primario flex-[2]"
          >
            Agregar al ticket
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Peso en vivo */}
        {hayBalanza && (
          <button
            type="button"
            disabled={!balanza?.estable || pesoBalanza === null || pesoBalanza === 0}
            onClick={() => onConfirmar({ cantidadMilesimas: pesoBalanza ?? 0 })}
            className={clsx(
              'flex w-full items-center justify-between gap-3 rounded-xl border p-4 text-left transition',
              balanza?.estable && pesoBalanza
                ? 'border-verde bg-verde-tenue hover:shadow-sm'
                : 'border-borde bg-crema-alt',
            )}
          >
            <span className="flex items-center gap-2.5">
              <Scale className="h-5 w-5 text-verde-oscuro" />

              <span>
                <span className="block text-[13px] font-medium text-tinta-suave">
                  Peso de la balanza
                </span>

                <span className="block text-[12px] text-tinta-tenue">
                  {!balanza?.hayLectura
                    ? 'Sin lectura: apoyá la mercadería'
                    : balanza.cero
                      ? 'La balanza está en cero'
                      : balanza.estable
                        ? 'Tocá para usar este peso'
                        : 'Esperando que se estabilice…'}
                </span>
              </span>
            </span>

            <span
              className={clsx(
                'num text-[26px] font-bold',
                balanza?.estable ? 'text-verde-oscuro' : 'text-tinta-tenue',
              )}
            >
              {pesoBalanza !== null ? cantidadMilesimas(pesoBalanza, 3) : '—'}
              <span className="ml-1 text-[15px] font-medium">kg</span>
            </span>
          </button>
        )}

        {/* Modo */}
        <div className="flex gap-2">
          <Solapa activa={modo === 'cantidad'} onClick={() => setModo('cantidad')}>
            <span className="opacity-40">←</span> Por {producto.unidadNombre.toLowerCase()}
          </Solapa>

          <Solapa activa={modo === 'importe'} onClick={() => setModo('importe')}>
            <Wallet className="h-4 w-4" /> Por importe <span className="opacity-40">→</span>
          </Solapa>
        </div>

        {/* Valor */}
        <div className="rounded-xl border border-borde bg-crema-alt p-4 text-center">
          <input
            ref={refInput}
            // inputMode decimal para que el celular abra el teclado numérico,
            // y readOnly para que en la caja táctil el teclado del sistema no
            // tape la pantalla: se carga con el teclado de acá.
            inputMode="decimal"
            value={formatearEntrada(digitos, modo, decimales)}
            readOnly
            aria-label={modo === 'importe' ? 'Importe' : 'Cantidad'}
            className="num w-full bg-transparent text-center text-[38px] font-bold text-tinta outline-none"
          />

          <p className="mt-1 text-[13px] text-tinta-tenue">
            {modo === 'importe'
              ? milesimasPorImporte(centavos, producto)
              : milesimas === 0 && pesoUsable
                ? // Con la balanza estable y nada tipeado, Enter toma el peso:
                  // se dice acá porque el número grande sigue en cero y sin el
                  // renglón parece que no hay nada cargado.
                  `Enter agrega el peso de la balanza (${cantidadMilesimas(pesoBalanza ?? 0, 3)} kg)`
                : `= ${plataCentavos(importeLinea(producto.precioCentavos, milesimas))}`}
          </p>
        </div>

        {/* Teclado */}
        <div className="grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <Tecla key={d} onClick={() => setDigitos((v) => (v + d).slice(0, 9))}>
              {d}
            </Tecla>
          ))}

          <Tecla onClick={() => setDigitos((v) => v.slice(0, -1))} aria-label="Borrar">
            <Delete className="mx-auto h-5 w-5" />
          </Tecla>

          <Tecla onClick={() => setDigitos((v) => (v + '0').slice(0, 9))}>0</Tecla>

          <Tecla onClick={() => setDigitos('')} aria-label="Limpiar">
            C
          </Tecla>
        </div>

        {/* Atajos de cantidades habituales. Es lo que evita tipear cuatro
            dígitos para el pedido más común del día. */}
        {modo === 'cantidad' && decimales > 0 && (
          <div className="flex flex-wrap gap-2">
            {[250, 500, 1000, 1500, 2000, 3000].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setDigitos(String(g))}
                className="rounded-lg border border-borde bg-papel px-3 py-2 text-[13px]
                           font-medium text-tinta-suave hover:border-verde"
              >
                {g < 1000 ? `${g} g` : `${g / 1000} kg`}
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

function Solapa({
  activa,
  onClick,
  children,
}: {
  activa: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2.5 text-[14px] font-medium transition',
        activa
          ? 'border-verde bg-verde-tenue text-verde-oscuro'
          : 'border-borde bg-papel text-tinta-suave hover:border-borde-fuerte',
      )}
    >
      {children}
    </button>
  )
}

function Tecla({
  onClick,
  children,
  ...resto
}: { onClick: () => void; children: React.ReactNode } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      // El `mousedown` no roba el foco: sin esto, tocar una tecla con el mouse
      // deja el foco en el BOTÓN, y desde ahí un medio de pago con tecla rápida
      // de un solo dígito se pelearía con lo que se tipea. Con el foco quieto
      // en el campo del importe, el dígito es siempre del importe y la tecla
      // del medio sigue andando con las de función.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      {...resto}
      // 56 px de alto: el mismo criterio que el PIN pad. Con menos, en una
      // pantalla táctil de mostrador se erra el dígito.
      className="num h-14 rounded-xl border border-borde bg-papel text-[20px] font-semibold
                 text-tinta transition hover:border-verde active:scale-95"
    >
      {children}
    </button>
  )
}

/** Muestra los dígitos con la coma en el lugar de la unidad. */
function formatearEntrada(digitos: string, modo: Modo, decimales: number): string {
  if (!digitos) return modo === 'importe' ? '$0' : '0'

  // `plataCorta` y no `toLocaleString` a mano: el separador de miles sale del
  // mismo lugar que en el resto de la pantalla. Con el formateo local, un
  // navegador sin la locale es-AR mostraba «1,000» acá y «$1.000,00» dos
  // renglones más abajo, en la misma venta.
  if (modo === 'importe') return plataCorta(Number(digitos))
  if (decimales === 0) return digitos

  const relleno = digitos.padStart(decimales + 1, '0')
  const entera = relleno.slice(0, -decimales)
  const decimal = relleno.slice(-decimales)

  return `${Number(entera)},${decimal}`
}

// La cantidad que sale de un importe la calcula `cantidadPorImporte` de
// lib/calculoVenta y no una copia local: es una cuenta de PLATA —decide cuánta
// mercadería se entrega por lo que el cliente pidió— y estaba escrita dos
// veces, acá y en el servidor. Es el gotcha que el sistema ya pagó con el
// importe de línea: dos implementaciones equivalentes hasta el día que alguien
// toca una, y el síntoma sería el cartel diciendo un peso y el ticket otro.
function milesimasPorImporte(centavos: number, p: ProductoPos): string {
  if (p.precioCentavos <= 0) return '—'

  const milesimas = cantidadPorImporte(centavos, p.precioCentavos, p.decimalesCantidad)

  return `≈ ${cantidadMilesimas(milesimas, p.decimalesCantidad)} ${p.unidadAbreviatura}`
}
